import React, { useState } from "react";
import ComponenteCampos, { ICONO_COMPONENTE } from "./ComponenteCampos";
import { useDialog } from "../context/dialogContext";

// Opción de la ficha como tarjeta con interruptor (título + explicación).
function OpcionFicha({ activa, onChange, titulo, children, peligro = false }) {
  return (
    <label className={`opcion-ficha ${activa ? "on" : ""} ${peligro ? "peligro" : ""}`}>
      <input type="checkbox" checked={activa} onChange={(e) => onChange(e.target.checked)} />
      <span className="of-switch" aria-hidden="true"><span /></span>
      <span className="of-txt">
        <b>{titulo}</b>
        <span>{children}</span>
      </span>
    </label>
  );
}

const NOMBRE_TIPO = { portal: "Portal", piso: "Piso", buzon: "Buzón" };
const NOMBRE_MEC = { fisica: "física", magnetica: "magnética", ambas: "ambas" };

// Estado inicial a partir de la SUGERENCIA (la variante más usada de cada
// tipo, de GET /piso_defaults/<id>) — o los defaults "de fábrica" (solo
// piso, física) si el piso no tiene ninguna variante conocida todavía.
function datosIniciales(sugerencia) {
  const comp = {
    portal: { tiene: false, mec: "fisica", tarj: false, seg: false },
    piso: { tiene: true, mec: "fisica", tarj: false, seg: false },
    buzon: { tiene: false, mec: "fisica", tarj: false, seg: false },
  };
  let segProveedor = "";
  if (sugerencia) {
    for (const tipo of ["portal", "piso", "buzon"]) {
      const s = sugerencia[tipo];
      if (!s) continue;
      comp[tipo] = { tiene: true, mec: s.mecanismo, tarj: !!s.tarjeta, seg: !!s.es_seguridad };
      if (s.es_seguridad && !segProveedor) {
        segProveedor = s.proveedor || "";
      }
    }
  }
  return { comp, segProveedor };
}

// Ficha de una llave nueva (portal/piso/buzón + seguridad): se usa cuando un
// código de llave no existe todavía para un piso y hay que confirmar y
// describirla antes de darla de alta — en Rutas>Preparar, en Prestar (cuando
// no quedan huecos libres) y en Administración>Crear juego. Mismos campos
// que espera el backend (helpers.crear_detalle_llaves).
//
// `defaults` (de GET /piso_defaults/<piso_id>) = {sugerencia, variantes}:
// la ficha arranca precargada con la variante más usada de cada tipo, y se
// explica arriba qué otras variantes existen ya para ese piso (un piso puede
// tener varias a la vez, p.ej. portal física en unos llaveros y magnética en
// otros).
//
// `peligroso` = true cuando esto va a crear un juego DE VERDAD nuevo (sube
// el total de llaves reales del piso) — a diferencia de solo "aprender" un
// código en un hueco ya contado, que no cambia el total. Se avisa mucho más
// fuerte en el primer caso: es el error más caro (duplicar sin darse cuenta).
// conUnidades: muestra el campo "Unidades" (cuántos juegos iguales crear de
// una vez). Solo en Administración > Crear juego, donde el número se asigna
// solo; en Prestar/Rutas el número lo teclea el operario y es siempre 1.
// piso (opcional): {id, codigo} — se muestra destacado en la cabecera.
const ICONO_TIPO_F = { portal: "🚪", piso: "🏠", buzon: "📮" };
const ICONO_MEC_F = { fisica: "🗝️", magnetica: "🧲", ambas: "🗝️🧲" };

export default function FichaLlaveModal({ mensaje, defaults, peligroso, onCancel, onConfirmar, conUnidades = false, piso = null }) {
  const sugerencia = defaults?.sugerencia;
  const variantes = defaults?.variantes || [];
  const iniciales = React.useMemo(() => datosIniciales(sugerencia), [sugerencia]);
  const [comp, setComp] = useState(iniciales.comp);
  const [segProveedor, setSegProveedor] = useState(iniciales.segProveedor);
  const [guardarDefault, setGuardarDefault] = useState(false);
  const [aplicarPiso, setAplicarPiso] = useState(false);
  const [comentario, setComentario] = useState("");
  const [unidades, setUnidades] = useState("1");
  const nUnidades = Math.min(50, Math.max(1, parseInt(unidades, 10) || 1));

  const set1 = (k, campo, v) => setComp((prev) => ({ ...prev, [k]: { ...prev[k], [campo]: v } }));
  const haySeguridad = comp.portal.seg || comp.piso.seg || comp.buzon.seg;

  const { confirmarPeligro } = useDialog();

  const confirmar = async () => {
    // "Aplicar a todas" reescribe las demás llaves del piso: se confirma aparte.
    if (aplicarPiso && !(await confirmarPeligro(
      "Vas a cambiar los componentes de TODAS las demás llaves activas de este piso para que sean " +
      "iguales que esta.\n\nHazlo solo si has comprobado que todas las llaves del piso son así. " +
      "No se puede deshacer de golpe.",
      { titulo: "Aplicar a todas las llaves del piso", boton: "Sí, aplicar a todas" }))) return;
    onConfirmar({
      confirmado_nueva_llave: true,
      tiene_portal: comp.portal.tiene, mec_portal: comp.portal.mec, tarj_portal: comp.portal.tarj, seg_portal: comp.portal.seg,
      tiene_piso: comp.piso.tiene, mec_piso: comp.piso.mec, tarj_piso: comp.piso.tarj, seg_piso: comp.piso.seg,
      tiene_buzon: comp.buzon.tiene, mec_buzon: comp.buzon.mec, tarj_buzon: comp.buzon.tarj, seg_buzon: comp.buzon.seg,
      seg_proveedor: segProveedor.trim() || null,
      guardar_default: guardarDefault, aplicar_a_piso: aplicarPiso,
      comentario_llave: comentario.trim() || null,
      ...(conUnidades ? { unidades: nUnidades } : {}),
    });
  };

  // Función (no componente <Fila/>): así React no recrea la fila en cada
  // tecla y los campos no pierden el foco.
  const filaComponente = (k, nombre) => (
    <div key={k} className={`componente-row t-${k}${comp[k].tiene ? " activo" : ""}`}>
      <label className="componente-row-head">
        <input type="checkbox" checked={comp[k].tiene} onChange={(e) => set1(k, "tiene", e.target.checked)} />
        <span className="cr-ico">{ICONO_COMPONENTE[k]}</span>
        <span className="cr-nombre">{nombre}</span>
        <span className="cr-estado">{comp[k].tiene ? "✓ Incluido" : "+ Añadir"}</span>
      </label>
      {comp[k].tiene && (
        <ComponenteCampos tipo={k} valor={comp[k]} onChange={(campo, v) => set1(k, campo, v)} />
      )}
    </div>
  );

  return (
    <div className="modal-back" onMouseDown={onCancel}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3>🔑 Nueva llave</h3>
        {piso && (
          <div className="ficha-piso">
            <span className="fp-lbl">Nueva llave para el piso</span>
            <span className="fp-piso"><span className="fp-id">{piso.id}</span>{piso.codigo}</span>
          </div>
        )}

        {peligroso ? (
          <div className="warn-box" style={{ marginBottom: 10 }}>
            <b>⚠️ Esto da de alta {conUnidades && nUnidades > 1 ? `${nUnidades} juegos de llaves NUEVOS` : "juegos de llaves NUEVOS"}</b>
          </div>
        ) : (
          <div className="seg-box" style={{ marginBottom: 10 }}>
            Se va a asignar este código a un hueco que el inventario ya contaba — no cambia el total de llaves del piso.
          </div>
        )}

        {mensaje && <div className="ficha-msg">{mensaje}</div>}

        {variantes.length > 0 && (
          <div className="llaveros">
            <div className="llaveros-tit">🧷 Llaveros que hay para este piso <span>solo referencia</span></div>
            <div className="llaveros-lista">
              {variantes.map((v, i) => (
                <div key={i} className={`llavero t-${v.tipo}`}>
                  <span className="ll-ico">{ICONO_TIPO_F[v.tipo] || "🔑"}</span>
                  <div className="ll-body">
                    <b>{NOMBRE_TIPO[v.tipo]}</b>
                    <span>
                      {ICONO_MEC_F[v.mecanismo] || ""} {NOMBRE_MEC[v.mecanismo]}{v.tarjeta ? " · tarjeta" : ""}
                      {v.es_seguridad ? ` · 🔒 seguridad${v.proveedor ? ` (${v.proveedor})` : ""}` : ""}
                    </span>
                    {v.comentario && <small>{v.comentario}</small>}
                  </div>
                  <span className="ll-n" title="Llaves que lo usan ahora">{v.n_llaves}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="seccion-nueva">
          <div className="sn-tit">
            <span className="sn-num">🧩</span>
            <div><b>¿Qué lleva la llave nueva?</b><small>Marca los componentes y elige su mecanismo</small></div>
          </div>
          {filaComponente("portal", "Portal")}
          {filaComponente("piso", "Piso")}
          {filaComponente("buzon", "Buzón")}
        </div>
        {haySeguridad && (
          <>
            <label>Proveedor de seguridad</label>
            <input value={segProveedor} onChange={(e) => setSegProveedor(e.target.value)} />
          </>
        )}

        <div className="opciones-ficha">
          <OpcionFicha activa={guardarDefault} onChange={setGuardarDefault} titulo="💾 Recordar esta configuración para el piso">
            Se guarda como uno de los llaveros habituales del piso: la próxima vez que des de alta una llave aquí,
            la ficha vendrá ya rellena con ella. <b>No cambia ninguna llave que ya exista.</b>
          </OpcionFicha>
          <OpcionFicha activa={aplicarPiso} onChange={setAplicarPiso} peligro titulo="⚠️ Aplicar a todas las demás llaves del piso">
            Cambia los componentes de <b>todas las demás llaves activas</b> de este piso para que sean iguales que esta.
            Úsalo solo si has comprobado que todas son así (te pedirá confirmación).
          </OpcionFicha>
        </div>

        {conUnidades && (
          <div className="unidades-box">
            <div className="ub-txt">
              <b>📦 Unidades a crear</b>
              <span>
                {nUnidades === 1 ? "Se creará 1 juego" : `Se crearán ${nUnidades} juegos iguales`},
                cada uno con el siguiente número libre del piso.
              </span>
            </div>
            <div className="unidades-ctrl">
              <button type="button" className="ub-btn" aria-label="Una menos"
                      onClick={() => setUnidades(String(Math.max(1, nUnidades - 1)))} disabled={nUnidades <= 1}>−</button>
              <input type="number" min="1" max="50" value={unidades} aria-label="Unidades a crear"
                     onChange={(e) => setUnidades(e.target.value)} onBlur={() => setUnidades(String(nUnidades))} />
              <button type="button" className="ub-btn" aria-label="Una más"
                      onClick={() => setUnidades(String(Math.min(50, nUnidades + 1)))} disabled={nUnidades >= 50}>+</button>
            </div>
          </div>
        )}

        <label style={{ marginTop: 10 }}>Comentario (opcional)</label>
        <textarea rows={2} value={comentario} maxLength={500} placeholder="Ej: copia hecha en ferretería X, llave de repuesto…"
                  onChange={(e) => setComentario(e.target.value)} />

        <div className="modal-actions">
          <button className="btn" onClick={onCancel}>Cancelar</button>
          <button className={peligroso ? "btn btn-danger" : "btn btn-primary"} onClick={confirmar}>
            {peligroso
              ? (conUnidades && nUnidades > 1 ? `Sí, crear ${nUnidades} juegos nuevos` : "Sí, crear juego nuevo")
              : "Confirmar y dar de alta"}
          </button>
        </div>
      </div>
    </div>
  );
}
